import { faCalendarCheck, faCircleDollar, faDoNotEnter, faEllipsisV, faHourglass, faPlus, faSearch, faSquareDollar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Collapse, Dropdown, Flex, Input, message, Skeleton, Switch, Typography } from 'antd';
import { useContext, useState } from 'react';
import { PricingType, PricingTypes } from '../../../../../common/constants';
import { useWorkareaGroupsQuery } from '../../../../../domain/workareas/groupsQueries';
import { useDebouncedState } from '../../../../../hooks/useDebouncedState';
import VSpace from '../../../../common/VSpace';
import { ItemsContext } from '../../../../Items/ItemsRoot';
import JobPlanContext from '../../JobPlanContext';
import CreateWorkareaModal from '../../List/CreateWorkareaModal';
import { buildGroupedWorkareas, countChildRecurringWorkareas, filterFirstOfEachPricingType, filterWorkareaGroups } from '../../List/workareaGroupsListModel';
import DeleteWorkareaModal from './DeleteWorkareaModal';
import DuplicateWorkareaModal from './DuplicateWorkareaModal';
import HiddenWorkareaCard from './HiddenWorkareaCard';
import JobDetails from './JobDetails';
import WorkareaGroupCard from './WorkareaGroupCard';
import MoveWorkareaModal from '../../List/MoveWorkareaModal';
import { defaultWorkarea } from '../../../../../domain/job';
import { moveWorkareaTo } from '../../List/dndModel';
import CreateWorkareaGroupModal from '../../List/CreateWorkareaGroupModal';
const { Text } = Typography

const WorkareasSection = () => {
  const { jobData: { jobId } } = useContext(ItemsContext)
  const { currentSelection, selectWorkarea, selectGroup, moveWorkareaOrGroup, isTotalsCalculating, setPendingAction } = useContext(JobPlanContext)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);

  const [selectedPricingTypeFilter, setSelectedPricingTypeFilter] = useState<PricingType[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isMoveWorkareaModalOpen, setIsMoveWorkareaModelOpen] = useState(false);
  const { debouncedState: debouncedSearchTerm, state: searchTerm, setState: setSearchTerm } = useDebouncedState<string | undefined>(undefined, 200);

  const workareaGroupsQuery = useWorkareaGroupsQuery(jobId)

  const state = workareaGroupsQuery.data
    ? {
      workareas: buildGroupedWorkareas(workareaGroupsQuery.data),
      workareaGroups: filterWorkareaGroups(workareaGroupsQuery.data, selectedPricingTypeFilter, debouncedSearchTerm)
    }
    : { workareas: {}, workareaGroups: [] }
  state.workareaGroups.sort((a, b) => a.sortIndex - b.sortIndex)

  const selectedWorkarea = state.workareaGroups.flatMap(x => x.workareas).find(w => w.id === currentSelection?.workareaId)
  const filtersList = filterFirstOfEachPricingType(workareaGroupsQuery.data ?? []).map(group => filtersObj[group.pricingType])

  const handleSelectedFilter = (checked: boolean, filter: PricingType) => {
    if (!checked) {
      setSelectedPricingTypeFilter(previousValue => previousValue.filter(item => item !== filter));
      return;
    }

    setSelectedPricingTypeFilter(previousValue => [...previousValue, filter]);
  }

  const handleWorkareaMovedTo = (groupId: string) => {
    moveWorkareaOrGroup(moveWorkareaTo(state, selectedWorkarea!, groupId))
    setIsMoveWorkareaModelOpen(false)
  }

  const handleSelectWorkarea = (workareaId: number) => {
    if (isTotalsCalculating) {
      message.loading('Waiting for totals calculations to complete...', 0)
      setPendingAction(() => () => {
        message.destroy()
        selectWorkarea(workareaId)
      })
      return
    }
    selectWorkarea(workareaId)
  }

  const handleSelectGroup = (groupId: string) => {
    if (isTotalsCalculating) {
      message.loading('Waiting for totals calculations to complete...', 0)
      setPendingAction(() => () => {
        message.destroy()
        selectGroup(groupId)
      })
      return
    }
    selectGroup(groupId)
  }

  if (workareaGroupsQuery.isLoading) {
    return <Skeleton active paragraph={{ rows: 5 }} />
  }

  const options = <Dropdown
    trigger={['click']}
    menu={{
      items: [
        {
          key: 'duplicate',
          label: 'Duplicate',
          onClick: () => setIsDuplicateModalOpen(true),
        },
        {
          key: 'delete',
          label: 'Delete',
          onClick: () => setIsDeleteModalOpen(true),
        },
        {
          key: 'move',
          label: 'Move',
          onClick: () => setIsMoveWorkareaModelOpen(true),
        }
      ]
    }}
  >
    <Button type="text" icon={<FontAwesomeIcon icon={faEllipsisV} />} />
  </Dropdown>

  return <div style={{ height: 'calc(100vh - 116px)', overflowY: 'auto', backgroundColor: 'white' }}>
    <Flex vertical gap={16}>
      <Flex vertical style={{ padding: 16, backgroundColor: '#f5f5f5' }} gap={16}>
        <JobDetails jobId={jobId} />
        <Input
          placeholder="Search"
          suffix={<FontAwesomeIcon icon={faSearch} />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <Collapse style={{ backgroundColor: 'white' }}
          items={[
            {
              id: 'filterBy',
              label: 'Filter By',
              children: <VSpace>
                <Text>Pricing Type</Text>
                {filtersList.map((filter) => <div
                  key={filter.id}
                  style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}
                >
                  <Switch style={{ marginRight: '8px' }} onChange={(checked) => handleSelectedFilter(checked, filter.id)} />
                  {filter.icon && <span style={{ marginRight: '8px' }}>{filter.icon}</span>}
                  <Text>{filter.label}</Text>
                </div>)}
              </VSpace>
            },
          ]}
        />
        <Flex justify="space-between" gap={8} style={{ width: '100%' }}>
          <Button
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => setIsCreateModalOpen(true)}
            style={{ flex: 1 }}
          >
            New Work Area
          </Button>
          <Button
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => setIsCreateGroupModalOpen(true)}
            style={{ flex: 1 }}
          >
            New Group
          </Button>
        </Flex>
      </Flex>

      <VSpace style={{ padding: 10 }}>
        {
          state.workareaGroups.map((group, index) => {
            return (group.isHidden && group.workareas.length > 0)
              ? <HiddenWorkareaCard
                key={index}
                group={group}
                cardOptions={options}
                onSelectWorkarea={handleSelectWorkarea}
                selectedWorkareaId={currentSelection?.workareaId}
              />
              : <WorkareaGroupCard
                key={index}
                group={group}
                cardOptions={options}
                groupWorkareas={group.workareas}
                onWorkareaSelection={handleSelectWorkarea}
                onGroupSelection={handleSelectGroup}
                selectedWorkareaId={currentSelection?.workareaId}
                selectedGroupId={currentSelection?.groupId}
              />
          })
        }
      </VSpace>
    </Flex>

    <DeleteWorkareaModal
      isOpen={isDeleteModalOpen}
      jobId={jobId}
      onCancel={() => setIsDeleteModalOpen(false)}
      workareaId={currentSelection?.workareaId}
      workareaName={selectedWorkarea?.name}
      isWorkareaGroupApproved={state.workareaGroups.find(x => x.workareas.some(w => w.id === currentSelection?.workareaId))?.isApproved ?? false}
      hasChildRecurringWorkareas={countChildRecurringWorkareas(state.workareas, currentSelection?.workareaId) > 0}
    />
    <DuplicateWorkareaModal
      isOpen={isDuplicateModalOpen}
      onCancel={() => setIsDuplicateModalOpen(false)}
      workarea={selectedWorkarea}
    />
    <CreateWorkareaModal
      jobId={jobId}
      groupId={undefined}
      isOpen={isCreateModalOpen}
      onCancel={() => {
        setIsCreateModalOpen(false)
      }}
      onWorkareaCreated={newWorkareaId => handleSelectWorkarea(newWorkareaId)}
    />
    <CreateWorkareaGroupModal
      isOpen={isCreateGroupModalOpen}
      onClose={() => setIsCreateGroupModalOpen(false)}
      jobId={jobId}
      onGroupCreated={handleSelectGroup}
    />
    <MoveWorkareaModal
      visible={isMoveWorkareaModalOpen}
      workarea={selectedWorkarea ?? defaultWorkarea()}
      workareaGroups={state.workareaGroups}
      onCancel={() => setIsMoveWorkareaModelOpen(false)}
      onSubmit={handleWorkareaMovedTo}
    />
  </div>
};

export default WorkareasSection;

const filtersObj: { [key: string]: { id: number, label: string, icon?: JSX.Element } } = {
  [PricingTypes.TimeAndMaterials]: { id: PricingTypes.TimeAndMaterials, label: 'Time & Materials', icon: <FontAwesomeIcon icon={faHourglass} /> },
  [PricingTypes.FlatRate]: { id: PricingTypes.FlatRate, label: 'Flat Rate', icon: <FontAwesomeIcon icon={faCircleDollar} /> },
  [PricingTypes.QuotedPrice]: { id: PricingTypes.QuotedPrice, label: 'Quoted', icon: <FontAwesomeIcon icon={faSquareDollar} /> },
  [PricingTypes.Unbillable]: { id: PricingTypes.Unbillable, label: 'Unbillable', icon: <FontAwesomeIcon icon={faDoNotEnter} /> },
  [PricingTypes.PerVisit]: { id: PricingTypes.PerVisit, label: 'Per Visit', icon: <FontAwesomeIcon icon={faCalendarCheck} /> }
}
